import { Cause, Effect, Exit, Layer, Match, Queue, Ref, Schema, Scope } from "effect";
import { ChunkRecoveryClock, ChunkRecoveryGuard, chunkRecoveryIoFailure, chunkReloadGuarded, type ChunkRecoveryIoFailure, type ChunkRecoveryResult } from "./model.ts";
import { ChunkRecovery, ChunkRecoveryIo } from "./service.ts";

export const makeChunkRecovery = Effect.fn("Frontend.ChunkRecovery.make")(function* (owner: Scope.Scope) {
  const io = yield* ChunkRecoveryIo, resources = yield* Scope.Scope;
  if (owner.state._tag === "Closed" || resources.state._tag === "Closed") return ChunkRecovery.of({ request: Effect.succeed("Closed") });
  const failures = yield* Ref.make<readonly Cause.Reason<never>[]>([]);
  const retain = (cause: Cause.Cause<ChunkRecoveryIoFailure>) => Ref.update(failures, current => [
    ...current, ...cause.reasons.flatMap(reason => reason._tag === "Fail" ? Cause.die(reason.error).reasons : [reason])
  ]);
  yield* Effect.addFinalizer(() => Ref.get(failures).pipe(Effect.flatMap(reasons => reasons.length ? Effect.failCause(Cause.fromReasons(reasons)) : Effect.void)));
  const signals = yield* Queue.unbounded<void>();
  const callbackFailures = yield* Queue.unbounded<ChunkRecoveryIoFailure>();
  // A native refusal cannot wait behind an accepted reload. Drain its receipt
  // before queue shutdown even when the signal loop is interrupted.
  yield* Effect.addFinalizer(() => Queue.clear(callbackFailures).pipe(Effect.flatMap(errors => Effect.forEach(errors, error => retain(Cause.fail(error)), { discard: true }))));
  const readNow = io.now.pipe(Effect.flatMap(value => Schema.decodeUnknownEffect(ChunkRecoveryClock)(value).pipe(Effect.mapError(chunkRecoveryIoFailure))));
  const request: Effect.Effect<ChunkRecoveryResult, ChunkRecoveryIoFailure> = Effect.gen(function* () {
    if (owner.state._tag === "Closed" || resources.state._tag === "Closed") return "Closed" as const;
    const guard = yield* io.readGuard.pipe(Effect.flatMap(value => Schema.decodeUnknownEffect(ChunkRecoveryGuard)(value).pipe(Effect.mapError(chunkRecoveryIoFailure))));
    if (chunkReloadGuarded(guard, yield* readNow)) return "Guarded" as const;
    const now = yield* readNow;
    const stored = yield* Effect.exit(io.writeGuard(now));
    if (Exit.isFailure(stored)) yield* retain(stored.cause);
    yield* io.reload;
    return "ReloadRequested" as const;
  }).pipe(Effect.uninterruptible, Effect.catchCause(cause => retain(cause).pipe(Effect.andThen(Effect.failCause(cause)))));
  yield* io.listen(notice => {
    Match.value(notice).pipe(
      Match.tag("ListenerFailure", value => { Queue.offerUnsafe(callbackFailures, value.error); }),
      Match.tag("PreloadError", () => {
        if (owner.state._tag !== "Closed" && resources.state._tag !== "Closed") Queue.offerUnsafe(signals, undefined);
      }),
      Match.exhaustive
    );
  });
  yield* Effect.forkIn(Effect.forever(Queue.take(signals).pipe(Effect.flatMap(() => Effect.exit(request).pipe(Effect.asVoid)))), owner);
  return ChunkRecovery.of({ request });
});
export const ChunkRecoveryLive = (owner: Scope.Scope) => Layer.effect(ChunkRecovery, makeChunkRecovery(owner).pipe(Effect.orDie));
